import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { FalconLogo } from "@/components/ui/FalconLogo";
import type { PublicSettings } from "@/types";

/**
 * Site footer.
 *
 * Phase 1: adds the falcon mark, uses the Playfair serif for column
 * headings and warm-beige hover for links. Impressum and
 * Barrierefreiheit live exclusively here (and on the legally required
 * pages) per specification 2.1.
 */
export function SiteFooter() {
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => apiGet<PublicSettings>("/public/settings"),
    staleTime: 10 * 60_000,
  });

  return (
    <footer className="bg-primary text-white mt-16">
      <div className="max-w-content mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <FalconLogo size={36} decorative className="ring-1 ring-white/30" />
            <h3 className="font-serif font-bold text-xl text-white">
              {settings?.site_name ?? "Falkenrehde"}
            </h3>
          </div>
          <p className="text-white/80 text-sm leading-relaxed italic">
            {settings?.site_tagline ?? "zu Hause im Havelland"}
          </p>
          {settings?.social && Object.keys(settings.social).length > 0 && (
            <div className="mt-4 flex gap-3">
              {Object.entries(settings.social).map(([name, url]) => (
                <a key={name} href={url} target="_blank" rel="noopener noreferrer"
                   className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-accent hover:text-text transition-colors"
                   aria-label={name}>
                  <SocialIcon name={name} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <h3 className="font-serif font-bold mb-3 text-white text-lg">{"Kontakt"}</h3>
          <address className="not-italic text-white/80 text-sm leading-relaxed">
            {settings?.contact?.name ?? "Ortsvorsteherin Falkenrehde"}<br />
            {settings?.contact?.address ?? "14669 Ketzin/Havel"}<br />
            {settings?.contact?.phone && <>Telefon: {settings.contact.phone}<br /></>}
            {settings?.contact?.email && (
              <a className="text-white hover:text-accent hover:underline transition-colors" href={"mailto:" + settings.contact.email}>{settings.contact.email}</a>
            )}
          </address>
        </div>
        <div>
          <h3 className="font-serif font-bold mb-3 text-white text-lg">{"Schnellzugriff"}</h3>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/aktuelles">{"Aktuelles"}</Link></li>
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/kalender">{"Kalender"}</Link></li>
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/dorfleben">Dorfleben</Link></li>
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/vereine">Vereine</Link></li>
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/kontakt">{"Kontakt"}</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="font-serif font-bold mb-3 text-white text-lg">{"Rechtliches"}</h3>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/impressum">{"Impressum"}</Link></li>
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/datenschutz">{"Datenschutz"}</Link></li>
            <li><Link className="hover:text-accent hover:underline transition-colors" to="/barrierefreiheit">{"Barrierefreiheit"}</Link></li>
            <li><a className="hover:text-accent hover:underline transition-colors" href="/sitemap.xml">Sitemap</a></li>
            <li><a className="hover:text-accent hover:underline transition-colors" href="/feed.xml">RSS-Feed</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/15">
        <div className="max-w-content mx-auto px-4 lg:px-6 py-4 text-xs text-white/70 flex flex-wrap items-center justify-between gap-3">
          <span>{"© " + new Date().getFullYear() + " Ortsteil Falkenrehde"}</span>
          <span><Link to="/admin" className="text-white/70 hover:text-accent hover:underline transition-colors">Redaktion</Link></span>
        </div>
      </div>
    </footer>
  );
}

function SocialIcon({ name }: { name: string }) {
  const key = name.toLowerCase();
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "currentColor" } as const;
  if (key.includes("facebook")) return (<svg {...common} aria-hidden="true"><path d="M13.5 21v-7h2.4l.4-3h-2.8V9.1c0-.9.3-1.5 1.6-1.5h1.4V5c-.7-.1-1.5-.2-2.3-.2-2.3 0-3.8 1.4-3.8 3.9V11H8v3h2.4v7h3.1Z"/></svg>);
  if (key.includes("instagram")) return (<svg {...common} aria-hidden="true"><path d="M12 7.5A4.5 4.5 0 1 0 16.5 12 4.5 4.5 0 0 0 12 7.5Zm0 7.5a3 3 0 1 1 3-3 3 3 0 0 1-3 3Z"/><path d="M17.5 5h-11A2.5 2.5 0 0 0 4 7.5v9A2.5 2.5 0 0 0 6.5 19h11a2.5 2.5 0 0 0 2.5-2.5v-9A2.5 2.5 0 0 0 17.5 5Zm.5 11.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1Z"/><circle cx="17.2" cy="7.3" r="1"/></svg>);
  if (key.includes("youtube")) return (<svg {...common} aria-hidden="true"><path d="M21.6 7.3a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.5A2.5 2.5 0 0 0 2.4 7.3 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.7 2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.5a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.7ZM10 15V9l5 3Z"/></svg>);
  return (<svg {...common} aria-hidden="true"><circle cx="12" cy="12" r="9" /></svg>);
}
